/**
 * Fixed set of Knowledge Base / Help Centre categories, mirrored from
 * server/kb-categories.js (kept in sync by hand, same convention as
 * client/src/config/blogCategories.js). "Property Guides" and "Policies"
 * are not KB categories - the Help Centre links out to those existing
 * systems instead (see HelpCentrePage.js).
 */
export const KB_CATEGORIES = [
  {
    slug: 'about',
    label: 'About NaijaNest',
    description: 'What NaijaNest is, how it works, and who can use it.',
    icon: '🏢',
  },
  {
    slug: 'finding-properties',
    label: 'Finding Properties',
    description: 'Browsing, searching, favourites, and reporting a listing.',
    icon: '🏠',
  },
  {
    slug: 'listing-properties',
    label: 'Listing a Property',
    description: 'Creating, editing and managing your property listings.',
    icon: '📝',
  },
  {
    slug: 'agents',
    label: 'Agents & Partners',
    description: 'Agent accounts and applying to become a NaijaNest partner.',
    icon: '👤',
  },
  {
    slug: 'messaging',
    label: 'Messaging',
    description: 'Contacting advertisers and staying safe while messaging.',
    icon: '💬',
  },
  {
    slug: 'reviews',
    label: 'Property Reviews',
    description: 'Leaving, reporting and moderating property reviews.',
    icon: '⭐',
  },
  {
    slug: 'bookings',
    label: 'Bookings & Reservations',
    description: 'Booking a short-let stay and requesting to reserve a property.',
    icon: '📅',
  },
  {
    slug: 'promote',
    label: 'Advertise & Earn',
    description: 'Advertising your business and earning rewards through Invite & Earn.',
    icon: '📣',
  },
  {
    slug: 'safety',
    label: 'Safety & Anti-Fraud',
    description: 'Staying safe, spotting fraud, and reporting suspicious activity.',
    icon: '🛡️',
  },
  {
    slug: 'account',
    label: 'Account & Profile',
    description: 'Registration, login, account settings and deletion.',
    icon: '⚙️',
  },
  {
    slug: 'support',
    label: 'Support',
    description: 'Contacting NaijaNest and getting help with an issue.',
    icon: '🆘',
  },
];

export const kbCategoryLabel = (slug) =>
  KB_CATEGORIES.find((c) => c.slug === slug)?.label || slug;

/**
 * The Help Centre links out to these existing static policy pages rather
 * than storing policy text as kb_articles (rewriting policy wording risks
 * changing its legal meaning). Labels mirror SiteFooter.js's `legal` list.
 */
// Agent Verification Policy is deliberately excluded - agent verification
// itself is gated (see memory: agent-verification-gated) and must not be
// referenced anywhere in the Help Centre while that's true.
export const POLICY_LINKS = [
  { path: '/terms', label: 'Terms of Service' },
  { path: '/listing-policy', label: 'Listing Policy' },
  { path: '/safety', label: 'Safety & Anti-Fraud' },
  { path: '/privacy', label: 'Privacy Policy' },
  { path: '/cookie-policy', label: 'Cookie Policy' },
];

export const policyLinkLabel = (path) =>
  POLICY_LINKS.find((p) => p.path === path)?.label || path;

/**
 * Mirrors server/kb-internal-links.js - the fixed whitelist of real
 * internal routes a KB article's cta_links can point to (internal_page and
 * action link types), plus the single Property Guide link. Keep in sync by
 * hand, same convention as the rest of this file.
 */
export const INTERNAL_PAGE_LINKS = [
  { url: '/browse', label: 'Browse Properties' },
  { url: '/list-property', label: 'List a Property' },
  { url: '/messages', label: 'Open Messages' },
  { url: '/favorites', label: 'Favourites' },
  { url: '/become-an-agent', label: 'Become a Partner' },
  { url: '/settings', label: 'Settings' },
  { url: '/contact', label: 'Contact Us' },
];

export const PROPERTY_GUIDE_LINK = { url: '/blog', label: 'Property Guide' };

export const internalPageLabel = (url) =>
  INTERNAL_PAGE_LINKS.find((l) => l.url === url)?.label || url;
